import * as React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CreditCard, Upload, Loader, AlertCircle } from "lucide-react";
import { authService } from "@/services/authService";
import { locationService } from "@/services/locationService";
import type { User } from "@/services/authService";
import { toast } from "sonner";

interface KtpAddressStepProps {
  userData: User | null;
  onSuccess: () => void;
  onError: (msg: string) => void;
  onConsentNeeded: (fn: () => Promise<void>, label: string) => void;
}

function validateKTPFormat(ktpValue: string): string | null {
  const trimmed = ktpValue.trim();
  if (!trimmed) return null;
  if (trimmed.length !== 16) return `KTP harus terdiri dari 16 digit`;
  if (!/^\d+$/.test(trimmed)) return "KTP harus berupa angka saja";
  return null;
}

export function KtpAddressStep({ userData, onSuccess, onError, onConsentNeeded }: KtpAddressStepProps) {
  const [ktp, setKtp] = React.useState(userData?.ktp ?? "");
  const [alamat, setAlamat] = React.useState(userData?.alamat ?? "");
  const [provinsi, setProvinsi] = React.useState(userData?.provinsi ?? "");
  const [kabupaten, setKabupaten] = React.useState(userData?.kabupaten ?? "");
  const [kecamatan, setKecamatan] = React.useState(userData?.kecamatan ?? "");
  const [kecamatanSearch, setKecamatanSearch] = React.useState("");
  const [kelurahan, setKelurahan] = React.useState(userData?.kelurahan ?? "");
  const [rw, setRw] = React.useState(userData?.rw ?? "");
  const [rt, setRt] = React.useState(userData?.rt ?? "");
  const [kodePos, setKodePos] = React.useState(userData?.kode_pos ?? "");
  const [idcardFile, setIdcardFile] = React.useState<File | null>(null);
  const [idcardPreview, setIdcardPreview] = React.useState<string | null>(null);
  const [provinces, setProvinces] = React.useState<string[]>([]);
  const [cities, setCities] = React.useState<string[]>([]);
  const [kecamatanList, setKecamatanList] = React.useState<string[]>([]);
  const [showKecamatanDropdown, setShowKecamatanDropdown] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [isOcrProcessing, setIsOcrProcessing] = React.useState(false);

  // Sync location fields from userData when it changes
  React.useEffect(() => {
    if (userData?.provinsi) setProvinsi(userData.provinsi);
    if (userData?.kabupaten) setKabupaten(userData.kabupaten);
    if (userData?.kecamatan) setKecamatan(userData.kecamatan);
    if (userData?.kelurahan) setKelurahan(userData.kelurahan);
    if (userData?.rw) setRw(userData.rw);
    if (userData?.rt) setRt(userData.rt);
    if (userData?.kode_pos) setKodePos(userData.kode_pos);
  }, [userData?.provinsi, userData?.kabupaten, userData?.kecamatan, userData?.kelurahan, userData?.rw, userData?.rt, userData?.kode_pos]);

  // Load provinces on mount
  React.useEffect(() => {
    const loadProvinces = async () => {
      try {
        const prov = await locationService.fetchProvinces();
        setProvinces(prov);
      } catch (err) {
        console.error("Failed to load provinces:", err);
      }
    };
    loadProvinces();
  }, []);

  // Load cities when userData is available (initial load)
  React.useEffect(() => {
    const loadInitialCities = async () => {
      if (userData?.provinsi) {
        try {
          const city = await locationService.fetchCitiesByProvince(userData.provinsi);
          setCities(city);
        } catch (err) {
          console.error("Failed to load cities:", err);
        }
      }
    };
    loadInitialCities();
  }, [userData?.provinsi]);

  // Load cities when province changes
  React.useEffect(() => {
    const loadCities = async () => {
      if (provinsi) {
        try {
          const city = await locationService.fetchCitiesByProvince(provinsi);
          setCities(city);
          if (!kabupaten) {
            setKabupaten("");
          }
        } catch (err) {
          console.error("Failed to load cities:", err);
        }
      } else {
        setCities([]);
        setKabupaten("");
      }
    };
    loadCities();
  }, [provinsi]);

  // Load kecamatan when kabupaten changes
  React.useEffect(() => {
    const loadKecamatan = async () => {
      if (kabupaten && provinsi) {
        try {
          const kec = await locationService.fetchKecamatanByCity(kabupaten, provinsi);
          setKecamatanList(kec);
        } catch (err) {
          console.error("Failed to load kecamatan:", err);
        }
      } else {
        setKecamatanList([]);
      }
    };
    loadKecamatan();
  }, [kabupaten, provinsi]);

  // Extract KTP number using OCR
  const extractKtpFromImage = async (file: File) => {
    try {
      setIsOcrProcessing(true);

      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      const Tesseract = (await import("tesseract.js")).default;
      const result = await Tesseract.recognize(dataUrl, "ind");
      const text = result.data.text;

      const ktpMatch = text.match(/\b\d{16}\b/);

      if (ktpMatch) {
        setKtp(ktpMatch[0]);
        toast.success("Nomor KTP berhasil diekstrak");
        toast.info("Harap melakukan check dari informasi yang berhasil di extract dari KTP, karena memungkinkan ada perbedaan karena kualitas gambar yang di unggah", {
          duration: 5000,
        });
      } else {
        toast.info("Nomor KTP tidak ditemukan, silakan isi manual");
      }
    } catch (err) {
      console.error("[OCR] Error during OCR process:", err);
      toast.error("Gagal membaca KTP");
    } finally {
      setIsOcrProcessing(false);
    }
  };

  const handleFileChange = (file: File | null) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setIdcardFile(file);
      setIdcardPreview(reader.result as string);
      extractKtpFromImage(file);
    };
    reader.readAsDataURL(file);
  };

  const runSubmit = async () => {
    setIsLoading(true);
    try {
      await authService.updateProfile({
        ktp: ktp.trim() || null,
        alamat,
        provinsi,
        kabupaten,
        kecamatan: kecamatan.trim() || undefined,
        kelurahan: kelurahan.trim() || undefined,
        rw: rw.trim() || undefined,
        rt: rt.trim() || undefined,
        kode_pos: kodePos.trim() || undefined,
      });

      if (idcardFile) {
        await authService.uploadPhotos(undefined, idcardFile);
      }

      onSuccess();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Gagal menyimpan data KTP dan alamat";
      setError(msg);
      onError(msg);
      toast.error("Gagal menyimpan data KTP/alamat", { description: msg });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (ktp.trim()) {
      const formatError = validateKTPFormat(ktp);
      if (formatError) {
        setError(formatError);
        return;
      }
    }

    if (!alamat.trim()) {
      setError("Alamat tidak boleh kosong");
      return;
    }

    if (!provinsi) {
      setError("Provinsi harus dipilih");
      return;
    }

    if (!kabupaten) {
      setError("Kabupaten harus dipilih");
      return;
    }

    if (idcardFile) {
      onConsentNeeded(runSubmit, "Simpan dan unggah foto KTP");
      return;
    }

    await runSubmit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          Foto KTP
        </Label>
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
            disabled={isLoading || isOcrProcessing}
            className="hidden"
            id="idcard-input"
          />
          <label
            htmlFor="idcard-input"
            className="flex h-32 w-full items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground bg-muted/50 p-4 cursor-pointer hover:bg-muted transition-colors relative"
          >
            {isOcrProcessing && (
              <div className="absolute inset-0 flex items-center justify-center bg-muted/80 rounded-lg">
                <div className="flex flex-col items-center gap-2">
                  <Loader className="h-5 w-5 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Membaca KTP...</p>
                </div>
              </div>
            )}
            {idcardPreview ? (
              <img src={idcardPreview} alt="Preview" className="h-full object-cover rounded" />
            ) : (
              <div className="text-center">
                <Upload className="h-6 w-6 mx-auto text-muted-foreground mb-1" />
                <p className="text-xs text-muted-foreground">Klik untuk upload foto KTP</p>
              </div>
            )}
          </label>
        </div>
        {idcardFile && <p className="text-xs text-green-600">✓ {idcardFile.name}</p>}
        {isOcrProcessing && <p className="text-xs text-blue-600">Mengekstrak nomor KTP...</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="ktp" className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          Nomor KTP
        </Label>
        <Input
          id="ktp"
          type="text"
          inputMode="numeric"
          maxLength={16}
          value={ktp}
          onChange={(e) => {
            const digitsOnly = e.target.value.replace(/\D/g, "");
            setKtp(digitsOnly);
          }}
          placeholder="Masukkan 16 digit nomor KTP"
          disabled={isLoading}
        />
        <p className="text-xs text-muted-foreground">
          KTP harus terdiri dari 16 digit angka
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="alamat">Alamat Lengkap</Label>
        <textarea
          id="alamat"
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          placeholder="Masukkan alamat lengkap Anda"
          value={alamat}
          onChange={(e) => setAlamat(e.target.value)}
          required
          disabled={isLoading}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="rt">RT (Rukun Tetangga)</Label>
          <Input
            id="rt"
            type="text"
            inputMode="numeric"
            pattern="^[0-9]{0,3}$"
            value={rt}
            onChange={(e) => {
              const v = e.target.value;
              if (/^[0-9]{0,3}$/.test(v)) setRt(v);
            }}
            disabled={isLoading}
            placeholder="Contoh: 000 / 01 / 1"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="rw">RW (Rukun Warga)</Label>
          <Input
            id="rw"
            type="text"
            inputMode="numeric"
            pattern="^[0-9]{0,3}$"
            value={rw}
            onChange={(e) => {
              const v = e.target.value;
              if (/^[0-9]{0,3}$/.test(v)) setRw(v);
            }}
            disabled={isLoading}
            placeholder="Contoh: 00 / 01 / 1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="kelurahan">Kelurahan</Label>
          <Input
            id="kelurahan"
            type="text"
            placeholder="Kelurahan"
            value={kelurahan}
            onChange={(e) => setKelurahan(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="kecamatan">Kecamatan</Label>
          <div className="relative">
            <Input
              id="kecamatan"
              type="text"
              placeholder="Cari Kecamatan..."
              value={kecamatanSearch || kecamatan}
              onChange={(e) => {
                setKecamatanSearch(e.target.value);
                setShowKecamatanDropdown(true);
              }}
              onFocus={() => setShowKecamatanDropdown(true)}
              onBlur={() => setTimeout(() => setShowKecamatanDropdown(false), 200)}
              disabled={isLoading || !kabupaten}
            />
            {showKecamatanDropdown && kecamatanList.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 border border-input bg-background rounded-md shadow-md max-h-48 overflow-y-auto">
                {kecamatanList
                  .filter((k) =>
                    k.toLowerCase().includes((kecamatanSearch || kecamatan).toLowerCase())
                  )
                  .map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => {
                        setKecamatan(k);
                        setKecamatanSearch("");
                        setShowKecamatanDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-muted text-sm"
                    >
                      {k}
                    </button>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="kabupaten">Kota/Kabupaten (tidak dapat diganti)</Label>
        <select
          id="kabupaten"
          value={kabupaten}
          onChange={(e) => setKabupaten(e.target.value)}
          disabled={true}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Pilih Kabupaten/Kota</option>
          {cities.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="provinsi">Provinsi (tidak dapat diganti)</Label>
        <select
          id="provinsi"
          value={provinsi}
          onChange={(e) => setProvinsi(e.target.value)}
          disabled={true}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <option value="">Pilih Provinsi</option>
          {provinces.map((p) => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="kode_pos">Kode Pos</Label>
        <Input
          id="kode_pos"
          type="text"
          inputMode="numeric"
          maxLength={5}
          value={kodePos}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, "");
            if (v.length <= 5) setKodePos(v);
          }}
          placeholder="Contoh: 12345"
          disabled={isLoading}
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          <AlertCircle className="h-4 w-4" />
          <span>{error}</span>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" className="flex-1" disabled={isLoading}>
          {isLoading ? "Menyimpan..." : "Lanjutkan"}
        </Button>
      </div>
    </form>
  );
}
